import {yupResolver} from "@hookform/resolvers/yup";
import {useEffect} from "react";
import {FieldErrors, FormProvider, useForm} from "react-hook-form";
import * as Yup from "yup";

import FormErrorAlerts from "@/components/form/FormErrorAlerts.tsx";
import {HorizontalDivider} from "@/components/HorizontalDivider.tsx";
import {Icon} from "@/components/icon/Icon.tsx";
import {ServerErrors} from "@/lib/types/common.ts";
import {CredentialSecret, CredentialType, CredentialTypeDisplayDetails, Credentials} from "@/lib/types/credentials.ts";

import {CredentialFormDisplayNameDescriptionInputs} from "./CredentialFormComponents.tsx";
import {YupCredentialUpdateSecretsSchema, setServerErrors} from "./CredentialFormUtils.ts";
import {CredentialTypeFormFields} from "./CredentialTypeFormFeilds.tsx";

export const UpdateCredentialFormSchema = (credentialType: CredentialType) =>
  Yup.object().shape({
    display_name: Yup.string().required("This field is required."),
    description: Yup.string().optional(),
    username: Yup.string()
      .optional()
      .transform((v) => (v === "" ? undefined : v)),
    secret: YupCredentialUpdateSecretsSchema(credentialType),
  });

export interface UpdateCredentialFormValues {
  display_name: string;
  description?: string;
  username?: string;
  secret?: CredentialSecret;
}

interface CredentialUpdateFormProps {
  credential: Credentials;
  formId: string;
  onSubmit: (data: UpdateCredentialFormValues) => void;
  onError?: (errors: FieldErrors, event?: React.BaseSyntheticEvent) => void;
  serverErrors?: ServerErrors;
}

export const CredentialTypeDisplay = ({credentialType}: {credentialType: CredentialType}) => {
  const credentialTypeDetails = CredentialTypeDisplayDetails[credentialType];
  return (
    <div className="row">
      <div className="d-flex align-items-center text-muted">
        <Icon icon={credentialTypeDetails.icon} />
        <span className="ms-2">{credentialTypeDetails.label}</span>
      </div>
    </div>
  );
};

export const CredentialUpdateForm = ({
  credential,
  formId,
  onSubmit,
  onError,
  serverErrors,
}: CredentialUpdateFormProps) => {
  const methods = useForm<UpdateCredentialFormValues>({
    resolver: yupResolver<UpdateCredentialFormValues>(UpdateCredentialFormSchema(credential.credential_type)),
    defaultValues: {
      display_name: credential.display_name,
      description: credential.description,
      username: credential.username,
      secret: undefined,
    },
  });

  const {handleSubmit, setError} = methods;

  useEffect(() => {
    if (serverErrors) {
      setServerErrors(serverErrors, setError, "");
    }
  }, [serverErrors, setError]);

  return (
    <FormProvider {...methods}>
      <form id={formId} onSubmit={handleSubmit(onSubmit, onError)}>
        {serverErrors?.__all__ && <FormErrorAlerts errors={serverErrors?.__all__} />}
        <CredentialTypeDisplay credentialType={credential.credential_type} />
        <HorizontalDivider className={"my-3"} />
        <CredentialFormDisplayNameDescriptionInputs />
        <HorizontalDivider className={"my-3"} />
        <CredentialTypeFormFields
          usedSecretProperties={credential.used_secret_properties}
          hint={credential.hint}
          credentialType={credential.credential_type}
        />
      </form>
    </FormProvider>
  );
};
